import type { AxiosError } from 'axios';
import axios from 'axios';
import { LocalizedResponseError, ServerNotAvailableError } from '../errors';
import type { ApiResponse, BaseApiClientResponse, BasicApiClientError, ResponceError } from '../types';
import { DefaultMiddleware } from './DefaultMiddleware';

export type TransformedApiClientResponse = BaseApiClientResponse & {
    payload?: any;
    error?: ResponceError | LocalizedResponseError;
    config: any;
};

const wrapErrorResponse = (error: LocalizedResponseError): ApiResponse<void> => ({
    payload: undefined,
    error,
});

/**
 * Мидлвар для BaseApiClient, приводит ответ от REST сервиса к стандартизированному виду { payload, error , ... },
 * где payload содержит бизнес-данные сервиса, а error данные ошибки, если запрос отработал с ошибкой.
 * Кроме того, обрабатывает ошибки
 * 1. от REST сервиса - локализует текст ошибки (ошибка 401 обрабатывается особым образом, т.к. она инициирует механизм рефреша токена аутенификации)
 * 2. сетевые ошибки - при отсутсвии интернета или сетевой недоступности сервера (сервер не отвечает)
 */
export class ResponseTransformerMiddleware extends DefaultMiddleware {
    // allowToFailOn401 следует выставить в true если ошибку 401 следует обработывать на следующем уровне миддвара, например для рефреша токена
    public constructor(private allowToFailOn401 = false) {
        super(ResponseTransformerMiddleware.name);
    }

    public responseInterceptor(response: BaseApiClientResponse): TransformedApiClientResponse {
        return {
            ...response,
            payload: response.data,
            error: response.error ? LocalizedResponseError.fromResponce(response) : undefined,
            config: response.config,
        };
    }

    /* Todo
    Необходимо отрефакторить обработку ошибок
    Сейчас все ошибки скидываются в общую кучу

    Надо разделять, сейчас видно такие типы:
    1) Обрабатываемые серверные ошибки REST.
        Такие ошибки подлежат последующей обработке бизнес-логикой в месте вызова REST.
    2) Обрабатываемые клиентские исключения (запрос был прерван, сервер не доступен, таймаут на запрос превышен и т.п).
        Такие ошибки могут быть обработны как на уровне специфических бизнес-кейсов, так
        и на глобальном уровне приложения, специализированными обработчиками (auth-ошибки, оффлайн режим).
    3) Необрабатываемые ошибки (все другие).
        Такие ошибки обрабатываются на глобальном уровне приложения.

        Все три типа ошибок взаимпоисключающие, поэтому рефакторинг можно провести путем
        расширения поля payload.error
    */
    public errorResponseHandler(error: BasicApiClientError | ServerNotAvailableError): ApiResponse<void> {
        const isCancelled = axios.isCancel(error);
        if (isCancelled) console.log('isCancelled', error);

        // todo проверить надо ли этот кейс
        if (error instanceof ServerNotAvailableError) {
            throw error;
        }

        // такое состояние является индикатором физической недоступности сетевого ресурса (отключен сервер или сеть) - на это указавает статус запроса 0 и XMLHttpRequest readystate 4. Здесь проверяется только статус, т.к. readystate проверяется в axios.
        if (error?.request?.status === 0) {
            return wrapErrorResponse(new ServerNotAvailableError());
        }

        // если allowToFailOn401 включено, значит один из следующих по цепочке миддлваров будет обрабатывать эту ошибку как инициатор рефреша токена
        if (this.allowToFailOn401 && error.response?.status === 401) {
            throw error;
        }

        // ошибка CORS, программно брошенная ошибка и т.п.
        if (error.isAxiosError === undefined) {
            console.error(error);

            return wrapErrorResponse(new LocalizedResponseError());
        }

        // ошибки сервиса (в т.ч. серверная валидация)
        const errors = (error as AxiosError<ResponceError>)?.response?.data?.errors;
        if (Array.isArray(errors)) {
            return wrapErrorResponse(
                new LocalizedResponseError({
                    statusCode: error?.response?.status || 0,
                    errors,
                }),
            );
        }

        // любые другие ошибки, в т.ч. ошибки сервиса без поля data.errors
        return wrapErrorResponse(
            new LocalizedResponseError({
                errors: [],
                statusCode: error?.response?.status || 0,
            }),
        );
    }
}
